import React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { AgentProviderConnection, AgentSettings, AgentTaskKey } from '../../types';
import { agentService } from '../../services/agent';
import { capabilityLabel, providerCapabilities, routeCompatibility, validateAgentTaskRouting } from '../../utils/aiRoutingValidation';

const agentTaskOptions: AgentTaskKey[] = ['DRAFT_ASSIST', 'THREAD_SUMMARY', 'VOICE_TRANSCRIPT', 'DOCUMENT_EXTRACTION', 'CLASSIFICATION'];

const providerDefaults = (type: string) => {
  if (type === 'OPENROUTER') {
    return {
      baseUrl: 'https://openrouter.ai/api/v1',
      model: 'openrouter/free',
      helper: 'OpenRouter is good for general reply help and lightweight routing. Use `openrouter/free` for low-cost text tasks.',
    };
  }
  if (type === 'GEMINI') {
    return {
      baseUrl: '',
      model: 'gemini-1.5-pro',
      helper: 'Gemini is a strong choice for multimodal work such as transcripts and document extraction.',
    };
  }
  if (type === 'DEEPSEEK') {
    return {
      baseUrl: 'https://api.deepseek.com',
      model: 'deepseek-chat',
      helper: 'Direct DeepSeek works well for text reasoning and reply generation. Use `deepseek-chat` unless you need `deepseek-reasoner`.',
    };
  }
  if (type === 'OPENAI') {
    return {
      baseUrl: 'https://api.openai.com/v1',
      model: 'gpt-4.1-mini',
      helper: 'OpenAI is reserved for direct GPT routing. Configure only if you have a direct OpenAI key.',
    };
  }
  if (type === 'ANTHROPIC') {
    return {
      baseUrl: 'https://api.anthropic.com/v1',
      model: 'claude-3-5-sonnet-latest',
      helper: 'Anthropic is reserved for Claude routing. Configure only if you have a direct Anthropic key.',
    };
  }
  if (type === 'CUSTOM') {
    return {
      baseUrl: 'https://chat.blink.co.tz',
      model: 'small',
      helper: 'Custom supports routed backend endpoints for Whisper (VOICE_TRANSCRIPT) and OCR (DOCUMENT_EXTRACTION). Keep base URL on your service host and map tasks to the correct connection.',
    };
  }
  return {
    baseUrl: '',
    model: '',
    helper: 'This provider is not wired for live execution yet.',
  };
};

const taskLabel = (task: AgentTaskKey) => {
  if (task === 'DRAFT_ASSIST') return 'Draft Assist';
  if (task === 'THREAD_SUMMARY') return 'Thread Summary';
  if (task === 'VOICE_TRANSCRIPT') return 'Voice Transcript';
  if (task === 'DOCUMENT_EXTRACTION') return 'Document Extraction';
  return 'Classification';
};

const taskDescription = (task: AgentTaskKey) => {
  if (task === 'DRAFT_ASSIST') return 'Rewrites and improves WhatsApp & email drafts. Fires when operator clicks BLINK or auto-trigger activates.';
  if (task === 'THREAD_SUMMARY') return 'Summarises long WhatsApp threads when operator opens a conversation. Best with a fast, cheap model.';
  if (task === 'VOICE_TRANSCRIPT') return 'Transcribes voice notes and audio messages to text. Requires a multimodal provider (Gemini recommended).';
  if (task === 'DOCUMENT_EXTRACTION') return 'Extracts text and data from uploaded PDFs, images, and documents. Requires multimodal support.';
  return 'Classifies inbound messages by intent (booking, complaint, general enquiry). Used for auto-routing.';
};

type AITabProps = {
  agentSettings: AgentSettings;
  setAgentSettings: (next: AgentSettings) => void;
};

export const AITab: React.FC<AITabProps> = ({ agentSettings, setAgentSettings }) => {
  const [providerTestState, setProviderTestState] = React.useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [providerTestMessage, setProviderTestMessage] = React.useState('');
  const [providerTestModule, setProviderTestModule] = React.useState<'QUOTER' | 'SCHEDULED' | 'PLANNER' | 'FUEL' | 'WHATSAPP' | 'EMAIL'>('EMAIL');
  const [routingStatus, setRoutingStatus] = React.useState('');
  const [showApiKey, setShowApiKey] = React.useState(false);
  const providerConnections: AgentProviderConnection[] =
    Array.isArray(agentSettings.providerConnections) && agentSettings.providerConnections.length > 0
      ? agentSettings.providerConnections
      : [{ id: 'primary', label: 'Primary Provider', enabled: true, ...agentSettings.provider }];
  const [selectedConnectionId, setSelectedConnectionId] = React.useState<string>(providerConnections[0]?.id || 'primary');
  const selectedConnection = providerConnections.find(connection => connection.id === selectedConnectionId) || providerConnections[0];
  const selectedProviderGuide = providerDefaults(selectedConnection?.type || 'GEMINI');
  const routingValidation = validateAgentTaskRouting(agentSettings);
  const usageConnections = agentSettings.usage?.connections || {};
  const selectedUsage = selectedConnection ? usageConnections[selectedConnection.id] : null;

  React.useEffect(() => {
    if (!selectedConnectionId || !providerConnections.some(connection => connection.id === selectedConnectionId)) {
      setSelectedConnectionId(providerConnections[0]?.id || 'primary');
    }
  }, [providerConnections, selectedConnectionId]);

  const syncConnections = (nextConnections: AgentProviderConnection[]) => {
    const primary = nextConnections.find(connection => connection.id === 'primary') || nextConnections[0];
    setAgentSettings({
      ...agentSettings,
      providerConnections: nextConnections,
      provider: primary
        ? {
            type: primary.type,
            apiKey: '',
            baseUrl: primary.baseUrl,
            model: primary.model,
          }
        : agentSettings.provider,
    });
  };

  const updateConnection = (connectionId: string, patch: Partial<AgentProviderConnection>) => {
    const nextConnections = providerConnections.map(connection => {
      if (connection.id !== connectionId) return connection;
      const nextType = patch.type || connection.type;
      const defaults = providerDefaults(nextType);
      return {
        ...connection,
        ...patch,
        baseUrl: patch.baseUrl !== undefined ? patch.baseUrl : (!connection.baseUrl ? defaults.baseUrl : connection.baseUrl),
        model: patch.model !== undefined ? patch.model : (!connection.model ? defaults.model : connection.model),
      };
    });
    syncConnections(nextConnections);
  };

  const runProviderTest = async () => {
    try {
      setProviderTestState('testing');
      setProviderTestMessage('');
      const result = await agentService.testProvider(selectedConnection || agentSettings.provider, providerTestModule);
      setProviderTestState('success');
      setProviderTestMessage(`${result.provider} / ${result.model}: ${result.preview}`);
    } catch (error: any) {
      setProviderTestState('error');
      setProviderTestMessage(error?.response?.data?.error || error?.message || 'Provider test failed');
    }
  };

  React.useEffect(() => {
    if (routingValidation.ok) {
      setRoutingStatus('');
      return;
    }
    setRoutingStatus(routingValidation.invalidRoutes.map(route => `${taskLabel(route.task)}: ${route.message}`).join(' | '));
  }, [routingValidation]);

  return (
    <div className="grid grid-cols-1 2xl:grid-cols-[1.2fr_0.8fr] gap-8 animate-in slide-in-from-bottom-4">
      <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-10 space-y-8 min-w-0">
        <div className="border-b border-slate-50 pb-6">
          <h3 className="text-sm font-black text-slate-800 uppercase tracking-[0.2em]">AI Connections</h3>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">Provider keys, models, and task routing</p>
        </div>

        <div className="rounded-[2rem] border border-slate-100 bg-slate-50 p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Provider Connections</p>
              <p className="mt-1 text-sm font-medium text-slate-500">Create one connection per LLM, then assign each task to the most suitable one.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                const nextId = `connection-${Date.now()}`;
                const nextConnections = [
                  ...providerConnections,
                  {
                    id: nextId,
                    label: `Provider ${providerConnections.length + 1}`,
                    enabled: true,
                    type: 'OPENROUTER' as const,
                    apiKey: '',
                    baseUrl: providerDefaults('OPENROUTER').baseUrl,
                    model: providerDefaults('OPENROUTER').model,
                  },
                ];
                syncConnections(nextConnections);
                setSelectedConnectionId(nextId);
              }}
              className="px-4 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest bg-slate-900 text-white"
            >
              Add Connection
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {providerConnections.map(connection => (
              <button
                key={connection.id}
                type="button"
                onClick={() => setSelectedConnectionId(connection.id)}
                className={`px-4 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest border ${
                  selectedConnection?.id === connection.id
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-500 border-slate-200'
                }`}
              >
                {connection.label}
              </button>
            ))}
          </div>

          {selectedConnection ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 rounded-[1.5rem] border border-slate-200 bg-white p-5">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Connection Label</label>
                <input
                  type="text"
                  value={selectedConnection.label}
                  onChange={e => updateConnection(selectedConnection.id, { label: e.target.value })}
                  className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 outline-none"
                />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Provider</label>
                <select
                  value={selectedConnection.type}
                  onChange={e => updateConnection(selectedConnection.id, { type: e.target.value as any, baseUrl: providerDefaults(e.target.value).baseUrl, model: providerDefaults(e.target.value).model })}
                  className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 outline-none appearance-none"
                >
                  <option value="OPENROUTER">OpenRouter</option>
                  <option value="GEMINI">Gemini</option>
                  <option value="OPENAI">GPT / OpenAI</option>
                  <option value="DEEPSEEK">DeepSeek</option>
                  <option value="ANTHROPIC">Claude / Anthropic</option>
                  <option value="CUSTOM">Custom</option>
                  <option value="NONE">None</option>
                </select>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">API Key</label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={selectedConnection.apiKey || ''}
                    onChange={e => updateConnection(selectedConnection.id, { apiKey: e.target.value })}
                    placeholder="Enter API key"
                    className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 outline-none pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(v => !v)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showApiKey ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <p className="text-[9px] font-bold text-slate-400 ml-2">Leave unchanged to keep the existing key. Keys are stored securely in the backend.</p>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Model</label>
                <input
                  type="text"
                  value={selectedConnection.model}
                  onChange={e => updateConnection(selectedConnection.id, { model: e.target.value })}
                  className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 outline-none"
                />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Base URL (optional)</label>
                <input
                  type="text"
                  value={selectedConnection.baseUrl}
                  onChange={e => updateConnection(selectedConnection.id, { baseUrl: e.target.value })}
                  className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 outline-none"
                />
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Connection Status</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => updateConnection(selectedConnection.id, { enabled: !selectedConnection.enabled })}
                    className={`px-5 py-4 rounded-2xl text-[9px] font-black uppercase tracking-widest ${
                      selectedConnection.enabled ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'
                    }`}
                  >
                    {selectedConnection.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                  {selectedConnection.id !== 'primary' ? (
                    <button
                      type="button"
                      onClick={() => {
                        const nextConnections = providerConnections.filter(connection => connection.id !== selectedConnection.id);
                        syncConnections(nextConnections);
                        setSelectedConnectionId(nextConnections[0]?.id || 'primary');
                      }}
                      className="px-5 py-4 rounded-2xl text-[9px] font-black uppercase tracking-widest bg-rose-50 text-rose-700 border border-rose-200"
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="md:col-span-2 rounded-[1.25rem] border border-indigo-100 bg-indigo-50/40 px-4 py-3">
                <p className="text-[9px] font-black uppercase tracking-widest text-indigo-400">Security Note</p>
                <p className="mt-1 text-sm font-medium text-slate-600">
                  API keys are encrypted and stored only in the backend database. They are never exposed in API responses or browser storage.
                </p>
              </div>
              <div className="md:col-span-2 rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Usage</p>
                <div className="mt-3 grid grid-cols-2 xl:grid-cols-5 gap-3">
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Calls</div>
                    <div className="mt-2 text-lg font-black text-slate-800">{selectedUsage?.totalCalls || 0}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Input Tokens</div>
                    <div className="mt-2 text-lg font-black text-slate-800">{(selectedUsage?.promptTokens || 0).toLocaleString()}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Output Tokens</div>
                    <div className="mt-2 text-lg font-black text-slate-800">{(selectedUsage?.completionTokens || 0).toLocaleString()}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total Tokens</div>
                    <div className="mt-2 text-lg font-black text-slate-800">{(selectedUsage?.totalTokens || 0).toLocaleString()}</div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                    <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Last Used</div>
                    <div className="mt-2 text-sm font-bold text-slate-700">{selectedUsage?.lastUsedAt ? new Date(selectedUsage.lastUsedAt).toLocaleString() : 'Never'}</div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                <div>{selectedProviderGuide.helper}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {providerCapabilities(selectedConnection.type).length > 0 ? providerCapabilities(selectedConnection.type).map(capability => (
                    <span key={capability} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-widest text-slate-600">
                      {capabilityLabel(capability)}
                    </span>
                  )) : (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-amber-700">
                      No live capabilities wired
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {routingStatus ? (
          <div className="rounded-[1.5rem] border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-700">
            {routingStatus}
          </div>
        ) : null}

        <div className="rounded-[2rem] border border-slate-100 bg-slate-50 p-5 space-y-4">
          <div>
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Task Routing</p>
            <p className="mt-1 text-sm font-medium text-slate-500">Per AI task, choose which connection should execute it. This is where admin decides where each LLM is used.</p>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {agentTaskOptions.map(task => {
              const route = agentSettings.taskRouting?.[task] || { connectionId: 'primary', model: '' };
              const routedConnection = providerConnections.find(connection => connection.id === route.connectionId) || null;
              const compatibility = routeCompatibility(task, routedConnection);
              return (
                <div key={task} className="rounded-[1.5rem] border border-slate-200 bg-white p-4 space-y-3">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">{taskLabel(task)}</div>
                    <p className="mt-1 text-xs font-medium text-slate-400 leading-relaxed">{taskDescription(task)}</p>
                  </div>
                  <select
                    value={route.connectionId || 'primary'}
                    onChange={e => setAgentSettings({
                      ...agentSettings,
                      taskRouting: {
                        ...agentSettings.taskRouting,
                        [task]: { ...route, connectionId: e.target.value },
                      },
                    })}
                    className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 outline-none appearance-none"
                  >
                    {providerConnections.map(connection => (
                      <option key={connection.id} value={connection.id}>{connection.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={route.model || ''}
                    onChange={e => setAgentSettings({
                      ...agentSettings,
                      taskRouting: {
                        ...agentSettings.taskRouting,
                        [task]: { ...route, model: e.target.value },
                      },
                    })}
                    placeholder="Optional task-specific model override"
                    className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 outline-none"
                  />
                  <div
                    className={`rounded-2xl border px-4 py-3 text-sm font-medium ${
                      compatibility.ok
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-amber-200 bg-amber-50 text-amber-700'
                    }`}
                  >
                    {compatibility.message}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-10 space-y-8 min-w-0">
        <div className="border-b border-slate-50 pb-6">
          <h3 className="text-sm font-black text-slate-800 uppercase tracking-[0.2em]">Connection Testing</h3>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">Validate each provider before mapping it to live tasks</p>
        </div>

        <div className="space-y-3">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Test Connection</label>
          <select
            value={selectedConnection?.id || 'primary'}
            onChange={e => setSelectedConnectionId(e.target.value)}
            className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 outline-none appearance-none"
          >
            {providerConnections.map(connection => (
              <option key={connection.id} value={connection.id}>{connection.label}</option>
            ))}
          </select>
        </div>
        <div className="space-y-3">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Test Module</label>
          <select
            value={providerTestModule}
            onChange={e => setProviderTestModule(e.target.value as any)}
            className="w-full bg-slate-50 border-none rounded-2xl p-4 font-bold text-slate-800 outline-none appearance-none"
          >
            <option value="EMAIL">Email</option>
            <option value="WHATSAPP">WhatsApp</option>
            <option value="QUOTER">Charter</option>
            <option value="SCHEDULED">Scheduled</option>
            <option value="PLANNER">Planner</option>
            <option value="FUEL">Fuel</option>
          </select>
        </div>

        <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Legacy Primary Provider</div>
          <div className="mt-2 text-sm font-semibold text-slate-700">
            {agentSettings.provider.type} / {agentSettings.provider.model || 'default'}
          </div>
          <p className="mt-2 text-xs text-slate-500">Kept only as a backward-compatible fallback. Live task execution should use the routing above.</p>
        </div>

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => void runProviderTest()}
            disabled={providerTestState === 'testing' || !selectedConnection || selectedConnection.type === 'NONE'}
            className={`px-6 py-4 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all ${
              providerTestState === 'testing' ? 'bg-slate-200 text-slate-500' : 'bg-slate-900 text-white shadow-lg shadow-slate-200'
            } disabled:opacity-50`}
          >
            {providerTestState === 'testing' ? 'Testing Provider...' : 'Test Provider'}
          </button>
          <span className="text-xs font-medium text-slate-500">
            Tests the selected connection exactly as configured. Use this before assigning the connection to real tasks.
          </span>
          {providerTestState !== 'idle' && providerTestMessage ? (
            <div className={`rounded-2xl px-4 py-3 text-sm ${
              providerTestState === 'success'
                ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                : providerTestState === 'error'
                  ? 'border border-rose-200 bg-rose-50 text-rose-700'
                  : 'border border-slate-200 bg-slate-50 text-slate-600'
            }`}>
              {providerTestMessage}
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
};
